import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { interviewApi } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

const TIME_OPTIONS = Array.from({ length: 23 }, (_, i) => {
  const hour = Math.floor(i / 2) + 9;
  const min = i % 2 === 0 ? '00' : '30';
  return `${String(hour).padStart(2, '0')}:${min}`;
}); // 09:00 ~ 20:00

interface InterviewPageProps {
  applicant: any;
}

interface Proposal {
  id: number;
  proposed_by: 'ADMIN' | 'APPLICANT';
  proposed_dates: { date: string; time: string }[];
  message: string | null;
  selected_index: number | null;
  created_at: string;
}

interface InterviewerInfo {
  id: number;
  name: string;
  position: string;
}

interface InterviewData {
  id: number;
  status: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  zoom_url: string | null;
  interviewer: InterviewerInfo | null;
  proposals: Proposal[];
}

const InterviewPage: React.FC<InterviewPageProps> = ({ applicant }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [testResults, setTestResults] = useState<any[]>([]);
  const [interview, setInterview] = useState<InterviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // 일정 선택
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // 대안 제시
  const [showAlternative, setShowAlternative] = useState(false);
  const [altDates, setAltDates] = useState([{ date: '', time: '' }]);
  const [altMessage, setAltMessage] = useState('');

  useEffect(() => {
    loadData();
  }, [applicant]);

  const loadData = async () => {
    try {
      const data = await interviewApi.getInterviewInfo(applicant.id);
      setTestResults(data.testResults || []);
      setInterview(data.interview || null);
    } catch (err) {
      console.error('데이터 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return `${date.getMonth() + 1}월 ${date.getDate()}일 (${days[date.getDay()]})`;
  };

  const handleSelectDate = async () => {
    if (!interview || selectedIndex === null) return;
    const latestAdminProposal = [...(interview.proposals || [])]
      .reverse()
      .find((p) => p.proposed_by === 'ADMIN');
    if (!latestAdminProposal) return;

    setSubmitting(true);
    try {
      await interviewApi.selectDate(interview.id, latestAdminProposal.id, selectedIndex);
      toast.success('일정이 선택되었습니다. 관리자 확정을 기다려주세요.');
      setSelectedIndex(null);
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '선택에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleProposeDates = async () => {
    if (!interview) return;
    const validDates = altDates.filter((d) => d.date && d.time);
    if (validDates.length === 0) {
      toast.error('최소 1개의 일정을 입력해주세요.');
      return;
    }

    setSubmitting(true);
    try {
      await interviewApi.proposeDates(interview.id, validDates, altMessage || undefined);
      toast.success('대안 일정이 제출되었습니다.');
      setShowAlternative(false);
      setAltDates([{ date: '', time: '' }]);
      setAltMessage('');
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '제출에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInterview = async () => {
    if (!interview) return;
    const ok = await confirm({
      title: '면접을 취소하시겠습니까?',
      message: '이 작업은 되돌릴 수 없습니다.',
      confirmText: '취소하기',
      variant: 'danger',
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      await interviewApi.cancelInterview(interview.id);
      toast.success('면접이 취소되었습니다.');
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '취소에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const addAltDate = () => {
    if (altDates.length < 3) {
      setAltDates([...altDates, { date: '', time: '' }]);
    }
  };

  const removeAltDate = (index: number) => {
    if (altDates.length > 1) {
      setAltDates(altDates.filter((_, i) => i !== index));
    }
  };

  const updateAltDate = (index: number, field: 'date' | 'time', value: string) => {
    const updated = [...altDates];
    updated[index] = { ...updated[index], [field]: value };
    setAltDates(updated);
  };

  const getLatestAdminProposal = (): Proposal | undefined => {
    if (!interview?.proposals) return undefined;
    return [...interview.proposals]
      .reverse()
      .find((p) => p.proposed_by === 'ADMIN');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-400">로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-slate-800">적합성 평가 (면접)</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* 서면평가 결과 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">서면평가 결과</h2>
          {testResults.length === 0 ? (
            <div className="text-center py-6 text-slate-400">서면평가 결과가 없습니다.</div>
          ) : (
            <div className="space-y-4">
              {testResults.map((result) => (
                <div key={result.id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-700">{result.test_title}</span>
                    <span className="text-lg font-bold text-indigo-600">
                      {result.score !== null ? `${result.score}점` : '채점 중'}
                      <span className="text-sm text-slate-400 font-normal"> / {result.total_score}점</span>
                    </span>
                  </div>
                  {result.evaluation && (
                    <p className="text-sm text-slate-600 mt-2 p-3 bg-white rounded border border-slate-200">
                      {result.evaluation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 면접 일정 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">면접 일정</h2>

          {!interview ? (
            /* 면접 없음 */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-slate-700 font-medium">면접 일정 조율 대기 중입니다</p>
              <p className="text-sm text-slate-500 mt-2">담당자가 면접 일정을 제안하면 알림을 드립니다.</p>
            </div>
          ) : interview.status === 'PROPOSAL_SENT' ? (
            /* 관리자가 일정 제안 → 선택 또는 대안 */
            <div className="space-y-4">
              {(() => {
                const proposal = getLatestAdminProposal();
                if (!proposal) return null;
                return (
                  <>
                    {proposal.message && (
                      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <p className="text-sm text-indigo-700">{proposal.message}</p>
                      </div>
                    )}
                    <p className="text-sm text-slate-600">아래 일정 중 가능한 시간을 선택해주세요.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {proposal.proposed_dates.map((slot, idx) => (
                        <label
                          key={idx}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedIndex === idx
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-slate-200 hover:border-indigo-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="date_select"
                            checked={selectedIndex === idx}
                            onChange={() => setSelectedIndex(idx)}
                            className="sr-only"
                          />
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-800">{formatDate(slot.date)}</p>
                              <p className="text-sm text-slate-600">{slot.time}</p>
                            </div>
                            {selectedIndex === idx && (
                              <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>

                    <button
                      onClick={handleSelectDate}
                      disabled={selectedIndex === null || submitting}
                      className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? '선택 중...' : '이 일정으로 선택하기'}
                    </button>

                    <div className="text-center">
                      <button
                        onClick={() => setShowAlternative(!showAlternative)}
                        className="text-sm text-slate-500 hover:text-indigo-600 underline"
                      >
                        {showAlternative ? '취소' : '다른 일정을 제안하고 싶습니다'}
                      </button>
                    </div>

                    {showAlternative && (
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                        <p className="text-sm font-medium text-slate-700">대안 일정 제시</p>
                        {altDates.map((d, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input
                              type="date"
                              value={d.date}
                              onChange={(e) => updateAltDate(idx, 'date', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                            />
                            <select
                              value={d.time}
                              onChange={(e) => updateAltDate(idx, 'time', e.target.value)}
                              className="px-3 py-2 border rounded-lg text-sm"
                            >
                              <option value="">시간</option>
                              {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            {altDates.length > 1 && (
                              <button
                                onClick={() => removeAltDate(idx)}
                                className="text-red-400 hover:text-red-600"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                        {altDates.length < 3 && (
                          <button onClick={addAltDate} className="text-sm text-indigo-600 hover:text-indigo-700">
                            + 일정 추가 (최대 3개)
                          </button>
                        )}
                        <textarea
                          value={altMessage}
                          onChange={(e) => setAltMessage(e.target.value)}
                          placeholder="메시지 (선택사항)"
                          rows={2}
                          className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                        />
                        <button
                          onClick={handleProposeDates}
                          disabled={submitting}
                          className="w-full py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                        >
                          {submitting ? '제출 중...' : '대안 일정 제출'}
                        </button>
                      </div>
                    )}

                    <div className="pt-3 border-t border-slate-200">
                      <button
                        onClick={handleCancelInterview}
                        disabled={submitting}
                        className="w-full py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        면접 취소하기
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : interview.status === 'APPLICANT_SELECTED' ? (
            /* 지원자가 선택 완료 → 관리자 확정 대기 */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-slate-700 font-medium">일정을 선택하셨습니다</p>
              <p className="text-lg font-semibold text-indigo-700 mt-2">
                {interview.scheduled_date && formatDate(interview.scheduled_date)} {interview.scheduled_time}
              </p>
              <p className="text-sm text-slate-500 mt-2">관리자의 최종 확정을 기다리고 있습니다.</p>
              <button onClick={handleCancelInterview} disabled={submitting}
                className="mt-4 text-sm text-red-500 hover:text-red-700">면접 취소하기</button>
            </div>
          ) : interview.status === 'ALTERNATIVE_PROPOSED' ? (
            /* 지원자가 대안 제시 → 관리자 검토 중 */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-slate-700 font-medium">대안 일정을 제안하셨습니다</p>
              <p className="text-sm text-slate-500 mt-2">관리자가 검토 중입니다. 잠시만 기다려주세요.</p>
              <button onClick={handleCancelInterview} disabled={submitting}
                className="mt-4 text-sm text-red-500 hover:text-red-700">면접 취소하기</button>
            </div>
          ) : interview.status === 'CONFIRMED' ? (
            /* 확정된 면접 */
            <div className="space-y-4">
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-sm text-indigo-600 mb-1">확정된 면접 일정</p>
                <p className="text-xl font-bold text-indigo-800">
                  {interview.scheduled_date && formatDate(interview.scheduled_date)} {interview.scheduled_time}
                </p>
              </div>

              {interview.zoom_url && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 mb-2">Zoom 미팅 링크</p>
                  <a
                    href={interview.zoom_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Zoom 참여하기
                  </a>
                  <p className="text-xs text-blue-500 mt-2">면접 시작 5분 전에 입장해주세요.</p>
                </div>
              )}

              {interview.interviewer && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-3">면접관</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-indigo-700">
                        {interview.interviewer.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{interview.interviewer.name}</p>
                      <p className="text-xs text-slate-500">{interview.interviewer.position}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : interview.status === 'COMPLETED' ? (
            /* 면접 완료 */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-slate-700 font-medium">면접이 완료되었습니다</p>
              <p className="text-sm text-slate-500 mt-2">결과를 기다려주세요.</p>
            </div>
          ) : interview.status === 'CANCELLED' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-slate-700 font-medium">면접이 취소되었습니다</p>
              <p className="text-sm text-slate-500 mt-2">재면접을 원하시면 아래 버튼을 눌러주세요.</p>
              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: '재면접을 신청하시겠습니까?',
                    message: '관리자가 새로운 일정을 제안할 예정입니다.',
                    confirmText: '신청',
                  });
                  if (!ok) return;
                  setSubmitting(true);
                  try {
                    await interviewApi.requestReinterview(interview.id);
                    toast.success('재면접 신청이 완료되었습니다. 관리자의 일정 제안을 기다려주세요.');
                    loadData();
                  } catch (err: any) {
                    toast.error(err.response?.data?.message || '신청에 실패했습니다.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                className="mt-4 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {submitting ? '신청 중...' : '재면접 신청'}
              </button>
            </div>
          ) : null}
        </div>

        {/* 협의 히스토리 타임라인 */}
        {interview && interview.proposals && interview.proposals.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">일정 협의 히스토리</h3>
            <div className="space-y-4">
              {interview.proposals
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                .map((proposal, idx) => (
                <div key={proposal.id} className="relative pl-6">
                  <div className={`absolute left-0 top-1 w-3 h-3 rounded-full ${
                    proposal.proposed_by === 'ADMIN' ? 'bg-indigo-500' : 'bg-emerald-500'
                  }`} />
                  {idx < interview.proposals.length - 1 && (
                    <div className="absolute left-[5px] top-4 w-0.5 h-full bg-slate-200" />
                  )}
                  <div className="pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        proposal.proposed_by === 'ADMIN'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {proposal.proposed_by === 'ADMIN' ? '관리자 제안' : '지원자 제안'}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(proposal.created_at).toLocaleString('ko-KR')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {proposal.proposed_dates.map((d, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-1 rounded ${
                            proposal.selected_index === i
                              ? 'bg-indigo-100 text-indigo-700 font-medium ring-1 ring-indigo-300'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {formatDate(d.date)} {d.time}
                          {proposal.selected_index === i && ' (선택됨)'}
                        </span>
                      ))}
                    </div>
                    {proposal.message && (
                      <p className="text-xs text-slate-500 mt-1">{proposal.message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 면접 안내 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">면접 진행 안내</h3>
          <div className="space-y-4 text-sm">
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-slate-700">일정 조율</p>
                <p className="text-slate-500 text-xs mt-1">관리자가 제안한 일정 중 선택하거나 대안을 제시해주세요</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-slate-700">일정 확정</p>
                <p className="text-slate-500 text-xs mt-1">관리자가 최종 확정하면 면접관 정보를 안내드립니다</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-slate-700">면접 당일</p>
                <p className="text-slate-500 text-xs mt-1">Zoom 링크로 면접 진행 (약 30-40분)</p>
              </div>
            </div>
          </div>
        </div>

        {/* 문의 안내 */}
        <div className="bg-slate-50 rounded-xl p-4 text-center">
          <p className="text-sm text-slate-500">
            문의사항: <span className="font-medium text-slate-700">sungbae@grkcon.com</span>
          </p>
        </div>
      </main>
    </div>
  );
};

export default InterviewPage;
